import { useCallback, useEffect, useState } from 'react';
import { Link } from '../components';
import { formatClock } from '../format';
import { navigate } from '../router';
import { adminApi, errorText, type LessonDetail } from './api';
import { MaterialsEditor } from './materials';
import { Card, Field, SaveBar, Toggle, confirmAction, parseClock, useSaver } from './ui';
import { VideoUpload } from './video-upload';

/** Eine Lektion bearbeiten: Texte, Video, Sprungmarken, Materialien. */
export function LessonEditor({ courseId, lessonId }: { courseId: string; lessonId: string }) {
  const [lesson, setLesson] = useState<LessonDetail | null | undefined>(undefined);
  const reload = useCallback(() => void adminApi.getLesson(lessonId).then(setLesson).catch(() => setLesson(null)), [lessonId]);
  useEffect(reload, [reload]);

  if (lesson === undefined) return <p className="muted">Wird geladen …</p>;
  if (lesson === null)
    return (
      <>
        <h1>Lektion nicht gefunden</h1>
        <Link href={`/verwaltung/kurse/${courseId}`} className="button button--secondary">Zum Kurs</Link>
      </>
    );

  return (
    <>
      <div className="adm-head">
        <div>
          <Link href={`/verwaltung/kurse/${courseId}`} className="muted small">← Zum Kurs</Link>
          <h1>{lesson.title}</h1>
        </div>
      </div>
      <Card title="Video">
        <VideoUpload lessonId={lesson.id} video={lesson.video} onChanged={reload} />
      </Card>
      <LessonBasics lesson={lesson} onChanged={setLesson} />
      <Chapters lesson={lesson} onChanged={setLesson} />
      <Card title="Materialien zu dieser Lektion">
        <p className="muted small">Workbook-Seiten, Vorlagen, Links – erscheinen in der Lektion unter dem Text.</p>
        <MaterialsEditor courseId={lesson.courseId} lessonId={lesson.id} materials={lesson.materials} onChanged={reload} />
      </Card>
      <Card title="Weitere Aktionen">
        <button type="button" className="link-button link-button--danger" onClick={() => { if (confirmAction(`Lektion „${lesson.title}“ löschen? Der Fortschritt der Kunden zu dieser Lektion geht verloren.`)) void adminApi.deleteLesson(lesson.id).then(() => navigate(`/verwaltung/kurse/${courseId}`)); }}>
          Lektion löschen
        </button>
      </Card>
    </>
  );
}

function LessonBasics({ lesson, onChanged }: { lesson: LessonDetail; onChanged: (l: LessonDetail) => void }) {
  const initial = { title: lesson.title, shortTitle: lesson.shortTitle ?? '', speakers: lesson.speakers ?? '', body: lesson.body ?? '', duration: lesson.durationSeconds ? formatClock(lesson.durationSeconds) : '', preview: lesson.preview };
  const [form, setForm] = useState(initial);
  const saver = useSaver();
  useEffect(() => setForm({ title: lesson.title, shortTitle: lesson.shortTitle ?? '', speakers: lesson.speakers ?? '', body: lesson.body ?? '', duration: lesson.durationSeconds ? formatClock(lesson.durationSeconds) : '', preview: lesson.preview }), [lesson]);
  const dirty = JSON.stringify(form) !== JSON.stringify(initial);
  const videoReady = lesson.video?.status === 'ready';

  return (
    <Card
      as="form"
      title="Lektion"
      onSubmit={(e) => {
        e.preventDefault();
        const duration = form.duration.trim() ? parseClock(form.duration) : null;
        if (form.duration.trim() && duration === null) return saver.setError('Laufzeit bitte als Minuten:Sekunden, zum Beispiel 12:30.');
        void saver.run(async () => onChanged(await adminApi.updateLesson(lesson.id, { title: form.title, shortTitle: form.shortTitle || null, speakers: form.speakers || null, body: form.body || null, preview: form.preview, ...(videoReady ? {} : { durationSeconds: duration }) })));
      }}
    >
      <div className="grid2">
        <Field label="Titel" id="l-title">
          <input id="l-title" type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required maxLength={200} />
        </Field>
        <Field label="Kurztitel für die Kachel" id="l-short" hint="Zwei, drei Worte – steht groß auf dem Bild. Leer = Titel.">
          <input id="l-short" type="text" value={form.shortTitle} onChange={(e) => setForm({ ...form, shortTitle: e.target.value })} maxLength={80} />
        </Field>
        <Field label="Referenten" id="l-speakers" hint="Erscheint unter dem Titel.">
          <input id="l-speakers" type="text" value={form.speakers} onChange={(e) => setForm({ ...form, speakers: e.target.value })} maxLength={200} />
        </Field>
        <Field label="Laufzeit" id="l-duration" hint={videoReady ? 'Kommt vom Video.' : 'Minuten:Sekunden – wird vom Video überschrieben, sobald eins fertig ist.'}>
          <input id="l-duration" type="text" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} disabled={videoReady} placeholder="12:30" />
        </Field>
      </div>
      <Field label="Text" id="l-body" hint="Beschreibung unter dem Video. Absätze durch Leerzeilen trennen.">
        <textarea id="l-body" rows={6} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} maxLength={20000} />
      </Field>
      <Toggle checked={form.preview} onChange={(v) => setForm({ ...form, preview: v })} label="Frei als Vorschau" hint="Auch ohne Kauf ansehbar – auf der Kurs-Infoseite als Kostprobe." />
      <SaveBar saver={saver} dirty={dirty} />
    </Card>
  );
}

function Chapters({ lesson, onChanged }: { lesson: LessonDetail; onChanged: (l: LessonDetail) => void }) {
  const toRows = (l: LessonDetail) => l.chapters.map((c) => ({ time: formatClock(c.offsetSeconds), label: c.label }));
  const [rows, setRows] = useState(toRows(lesson));
  const saver = useSaver();
  useEffect(() => setRows(toRows(lesson)), [lesson]);
  const dirty = JSON.stringify(rows) !== JSON.stringify(toRows(lesson));
  const duration = lesson.video?.status === 'ready' ? (lesson.video.durationSeconds ?? null) : (lesson.durationSeconds ?? null);

  return (
    <Card
      as="form"
      title="Sprungmarken – Learnings aus dem Workbook"
      onSubmit={(e) => {
        e.preventDefault();
        const chapters: { offsetSeconds: number; label: string }[] = [];
        for (const r of rows) {
          if (!r.label.trim() && !r.time.trim()) continue;
          const s = parseClock(r.time);
          if (s === null) return saver.setError(`„${r.time}“ ist keine Zeit. Bitte Minuten:Sekunden, zum Beispiel 4:12.`);
          if (!r.label.trim()) return saver.setError('Jede Sprungmarke braucht eine Beschriftung.');
          chapters.push({ offsetSeconds: s, label: r.label.trim() });
        }
        chapters.sort((a, b) => a.offsetSeconds - b.offsetSeconds);
        void saver.run(async () => onChanged(await adminApi.setChapters(lesson.id, chapters)));
      }}
    >
      <p className="muted small">Ein Klick auf eine Sprungmarke springt im Video genau an diese Stelle. Zeit als Minuten:Sekunden.</p>
      <div className="chapter-rows">
        {rows.map((r, i) => {
          const s = parseClock(r.time);
          const beyond = s !== null && duration !== null && s >= duration;
          return (
            <div key={i} className={`chapter-row${beyond ? ' chapter-row--beyond' : ''}`}>
              <input type="text" value={r.time} placeholder="4:12" onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, time: e.target.value } : x)))} className="chapter-row__time" />
              <input type="text" value={r.label} placeholder="Was lernt man hier?" onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} maxLength={200} />
              <button type="button" className="icon-button" onClick={() => setRows(rows.filter((_, j) => j !== i))} aria-label="Entfernen">×</button>
              {beyond && <span className="error-inline small">liegt hinter dem Ende des Videos ({formatClock(duration!)})</span>}
            </div>
          );
        })}
      </div>
      <SaveBar saver={saver} dirty={dirty}>
        <button type="button" className="button button--secondary button--small" onClick={() => setRows([...rows, { time: '', label: '' }])}>
          Sprungmarke hinzufügen
        </button>
      </SaveBar>
    </Card>
  );
}
